/**
 * lib/validations/schemas.ts
 *
 * Centralized Zod validation schemas for forms throughout the application.
 * These schemas provide client-side validation with user-friendly error messages.
 */
import { z } from 'zod';

// ============================================================================
// Common field schemas (reusable)
// ============================================================================

export const emailSchema = z
  .string()
  .min(1, 'Email is required')
  .email('Please enter a valid email address');

export const passwordSchema = z
  .string()
  .min(1, 'Password is required')
  .min(8, 'Password must be at least 8 characters');

export const requiredString = (fieldName: string) =>
  z.string().min(1, `${fieldName} is required`);

export const optionalString = z.string().optional();

export const positiveNumber = (fieldName: string) =>
  z.number().positive(`${fieldName} must be a positive number`);

export const dateString = (fieldName: string) =>
  z.string().regex(/^\d{4}-\d{2}-\d{2}$/, `${fieldName} must be in YYYY-MM-DD format`);

export const iataCode = z
  .string()
  .length(3, 'IATA code must be exactly 3 characters')
  .regex(/^[A-Z]{3}$/, 'IATA code must be 3 uppercase letters');

// ============================================================================
// Authentication schemas
// ============================================================================

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
});

export type LoginFormData = z.infer<typeof loginSchema>;

// ============================================================================
// Booking schemas
// ============================================================================

/** Flight number pattern: 2-3 letter airline code + 1-4 digit flight number */
export const flightNumberSchema = z
  .string()
  .min(1, 'Flight number is required')
  .regex(/^[A-Z]{2,3}\d{1,4}[A-Z]?$/, 'Invalid flight number format (e.g., EY100)');

export const optionalFlightNumber = z
  .string()
  .refine(
    (val) => val === '' || /^[A-Z]{2,3}\d{1,4}[A-Z]?$/.test(val),
    'Invalid flight number format (e.g., EY100)'
  )
  .optional()
  .or(z.literal(''));

/** Schema for CreateBookingSheet form - matches actual RoomType, Occupancy, BookingSourceKind */
export const createBookingSheetSchema = z.object({
  hotel_id: requiredString('Hotel'),
  primary_crew_id: requiredString('Primary crew'),
  companion_crew_id: z.string().optional().or(z.literal('')),
  check_in_utc: z.string().min(1, 'Check-in date/time is required'),
  check_out_utc: z.string().min(1, 'Check-out date/time is required'),
  room_type: z.enum(['SINGLE', 'DOUBLE'], {
    errorMap: () => ({ message: 'Please select a room type' }),
  }),
  occupancy: z.enum(['SINGLE_USE', 'DOUBLE_OCCUPIED', 'DOUBLE_SINGLE_USE'], {
    errorMap: () => ({ message: 'Please select occupancy type' }),
  }),
  arrival_flight_number: flightNumberSchema,
  departure_flight_number: optionalFlightNumber,
  source_kind: z.enum(['AD_HOC_HOTEL', 'CONTRACTED_HOTEL', 'REZOLV_SOURCED_INVENTORY']).default('AD_HOC_HOTEL'),
}).refine(
  (data) => {
    const checkIn = new Date(data.check_in_utc);
    const checkOut = new Date(data.check_out_utc);
    return checkOut > checkIn;
  },
  {
    message: 'Check-out must be after check-in',
    path: ['check_out_utc'],
  }
).refine(
  (data) => {
    const checkIn = new Date(data.check_in_utc);
    const checkOut = new Date(data.check_out_utc);
    const diffMs = checkOut.getTime() - checkIn.getTime();
    const nights = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return nights >= 1;
  },
  {
    message: 'Booking must be at least 1 night',
    path: ['check_out_utc'],
  }
);

export type CreateBookingSheetFormData = z.infer<typeof createBookingSheetSchema>;

/** Legacy schema for backward compatibility */
export const createBookingSchema = z.object({
  primary_crew_id: requiredString('Primary crew'),
  companion_crew_id: optionalString,
  hotel_id: requiredString('Hotel'),
  station_iata: iataCode,
  check_in_utc: z.string().min(1, 'Check-in date is required'),
  check_out_utc: z.string().min(1, 'Check-out date is required'),
  room_type: z.enum(['STANDARD', 'SUPERIOR', 'SUITE'], {
    errorMap: () => ({ message: 'Please select a room type' }),
  }),
  occupancy: z.enum(['SINGLE_OCCUPIED', 'DOUBLE_OCCUPIED'], {
    errorMap: () => ({ message: 'Please select occupancy type' }),
  }),
  arrival_flight_number: optionalString,
  departure_flight_number: optionalString,
}).refine(
  (data) => new Date(data.check_out_utc) > new Date(data.check_in_utc),
  {
    message: 'Check-out must be after check-in',
    path: ['check_out_utc'],
  }
);

export type CreateBookingFormData = z.infer<typeof createBookingSchema>;

// ============================================================================
// Hotel schemas
// ============================================================================

export const hotelSchema = z.object({
  name: requiredString('Hotel name'),
  station_iata: iataCode,
  brand_group: requiredString('Brand group'),
  star_rating: z.number().min(1).max(5, 'Star rating must be between 1 and 5'),
  priority: z.enum(['P1', 'P2', 'P3', 'P4'], {
    errorMap: () => ({ message: 'Please select a priority' }),
  }),
  contracted_rooms: z.number().min(0, 'Contracted rooms cannot be negative'),
  contracted_rate: z.number().min(0, 'Rate cannot be negative'),
  currency: z.string().length(3, 'Currency must be 3 characters'),
  contact_email: emailSchema.optional().or(z.literal('')),
  contact_phone: optionalString,
});

export type HotelFormData = z.infer<typeof hotelSchema>;

// ============================================================================
// Invoice schemas
// ============================================================================

export const uploadInvoiceSchema = z.object({
  hotel_id: requiredString('Hotel'),
  invoice_number: requiredString('Invoice number'),
  invoice_date: dateString('Invoice date'),
  total_amount: positiveNumber('Total amount'),
  currency: z.string().length(3, 'Currency must be 3 characters'),
  billing_period_start: dateString('Billing period start'),
  billing_period_end: dateString('Billing period end'),
}).refine(
  (data) => new Date(data.billing_period_end) >= new Date(data.billing_period_start),
  {
    message: 'Billing period end must be on or after start date',
    path: ['billing_period_end'],
  }
);

export type UploadInvoiceFormData = z.infer<typeof uploadInvoiceSchema>;

// ============================================================================
// Broadcast schemas
// ============================================================================

export const broadcastSchema = z.object({
  title: requiredString('Title').max(100, 'Title must be 100 characters or less'),
  message: requiredString('Message').max(1000, 'Message must be 1000 characters or less'),
  audience_type: z.enum(['ALL', 'STATION', 'RANK', 'CUSTOM'], {
    errorMap: () => ({ message: 'Please select an audience type' }),
  }),
  stations: z.array(z.string()).optional(),
  ranks: z.array(z.string()).optional(),
  priority: z.enum(['LOW', 'NORMAL', 'HIGH', 'URGENT']).default('NORMAL'),
  requires_acknowledgement: z.boolean().default(false),
});

export type BroadcastFormData = z.infer<typeof broadcastSchema>;

// ============================================================================
// Feedback schemas
// ============================================================================

export const feedbackSchema = z.object({
  booking_id: requiredString('Booking'),
  category: z.enum(['ROOM_QUALITY', 'SERVICE', 'TRANSPORT', 'FOOD', 'OTHER'], {
    errorMap: () => ({ message: 'Please select a category' }),
  }),
  severity: z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']).default('MEDIUM'),
  description: requiredString('Description').min(10, 'Please provide more details'),
  crew_id: requiredString('Crew member'),
});

export type FeedbackFormData = z.infer<typeof feedbackSchema>;

// ============================================================================
// Search/Filter schemas
// ============================================================================

export const searchSchema = z.object({
  query: z.string().max(200, 'Search query too long'),
  station: z.string().optional(),
  status: z.string().optional(),
  date_from: z.string().optional(),
  date_to: z.string().optional(),
});

export type SearchFormData = z.infer<typeof searchSchema>;
